/** Fotos, vídeos, áudios e arquivos da mensagem — e o visualizador. */
import { useEffect, useRef, useState } from "react";

import { ROTULO_DO_TIPO, SIGLA_DO_TIPO, blobDoAnexo, tamanhoLegivel } from "../../services/chat";
import { ICONE, Ic } from "./Icones";
import { MicDoAudio } from "./Marcas";
import { comecouATocar, escolherVelocidade, parouDeTocar, proximaVelocidade, useVelocidadeDosAudios } from "./tocador";

export function tipoDoArquivo(arquivo) {
  const mime = (arquivo.type || "").toLowerCase();
  const nome = arquivo.name.toLowerCase();
  if (mime.startsWith("image/")) return "imagem";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf" || nome.endsWith(".pdf")) return "pdf";
  if ([".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".csv", ".txt", ".odt", ".ods"].some((e) => nome.endsWith(e))) return "documento";
  return "outro";
}

/**
 * `miniatura`: a foto pequena do balão. `nada`: não baixa (vídeo no balão —
 * baixar 200 MB só para a prévia travava a conversa; abre no visualizador).
 */
function useBlob(anexo, { miniatura = false, nada = false } = {}) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let vivo = true;
    setUrl(null);
    if (!anexo) return undefined;
    // Recém-enviado: a foto já está no aparelho, não precisa descer do servidor.
    if (anexo.local_url) { setUrl(anexo.local_url); return undefined; }
    if (nada) return undefined;
    blobDoAnexo(anexo, { miniatura }).then((u) => vivo && setUrl(u)).catch(() => undefined);
    return () => { vivo = false; };
    // Pelo ID: a cada releitura o anexo é um objeto novo, e o áudio que a
    // pessoa estava ouvindo parava quando alguém reagia a uma mensagem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anexo?.id, miniatura, nada]);
  return url;
}

function Miniatura({ anexo, aoAbrir }) {
  const video = anexo.tipo === "video";
  const url = useBlob(anexo, { miniatura: !!anexo.miniatura, nada: video });
  return (
    <button type="button" className={`pchat-midia ${video && !url ? "so-video" : ""}`} onClick={aoAbrir} aria-label={`Abrir ${anexo.nome}`}>
      {url ? (video ? <video src={url} muted preload="metadata" /> : <img src={url} alt={anexo.nome} loading="lazy" />) : video ? <span className="tocar" aria-hidden="true">▶</span> : null}
      <span className="legenda">{video ? `vídeo · ${tamanhoLegivel(anexo.tamanho)}` : tamanhoLegivel(anexo.tamanho)}</span>
    </button>
  );
}

export function minutosESegundos(segundos) {
  if (!Number.isFinite(segundos) || segundos < 0) return "0:00";
  const s = Math.floor(segundos);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Fase B: o áudio como no WhatsApp — play/pausa, quanto falta, barra que se
 * arrasta e 1×/1,5×/2×. O `<audio>` nativo não mostrava a duração e não tinha
 * velocidade. Continua tocando ao rolar: o balão não sai da tela por isso.
 * Um por vez e a mesma velocidade em todos (`tocador.js`).
 */
function Audio({ anexo, minha, aoOuvir }) {
  const url = useBlob(anexo);
  const avisouRef = useRef(false);
  const ref = useRef(null);
  const [tocando, setTocando] = useState(false);
  const [tempo, setTempo] = useState(0);
  const [duracao, setDuracao] = useState(anexo.duracao_s || 0);
  const velocidade = useVelocidadeDosAudios();
  useEffect(() => {
    if (ref.current) ref.current.playbackRate = velocidade;
  }, [velocidade, url]);
  useEffect(() => {
    const a = ref.current;
    return () => { if (a) parouDeTocar(a); };
  }, [url]);
  const alternar = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => undefined); else a.pause();
  };
  const trocarVelocidade = () => escolherVelocidade(proximaVelocidade(velocidade));
  const pular = (e) => {
    const a = ref.current;
    if (!a || !duracao) return;
    a.currentTime = (Number(e.target.value) / 1000) * duracao;
  };
  return (
    <div className="pchat-audio">
      <MicDoAudio ouvido={!!anexo.ouvido} tamanho={18} />
      <button type="button" className="pchat-btn" aria-label={tocando ? "Pausar" : "Ouvir"} disabled={!url} onClick={alternar}>
        <Ic d={tocando ? ICONE.pausa : ICONE.play} size={16} />
      </button>
      <input
        type="range" min="0" max="1000" value={duracao ? Math.round((tempo / duracao) * 1000) : 0}
        onChange={pular} aria-label="Posição do áudio" disabled={!url}
      />
      <small className="tempo">{url ? `${minutosESegundos(tocando || tempo ? tempo : duracao)}` : "…"}</small>
      <button type="button" className="pchat-chip velocidade" aria-label={`Velocidade ${velocidade}×`} onClick={trocarVelocidade}>{`${velocidade}×`.replace(".", ",")}</button>
      {url && (
        <audio
          ref={ref} src={url} preload="metadata"
          onLoadedMetadata={(e) => { if (Number.isFinite(e.target.duration)) setDuracao(e.target.duration); e.target.playbackRate = velocidade; }}
          onTimeUpdate={(e) => setTempo(e.target.currentTime)}
          onPlay={(e) => {
            comecouATocar(e.target);
            setTocando(true);
            // Tocou o áudio de outra pessoa pela primeira vez: o microfone dela fica azul.
            if (!minha && !anexo.ouvido && !avisouRef.current && aoOuvir) { avisouRef.current = true; aoOuvir(anexo); }
          }}
          onPause={(e) => { parouDeTocar(e.target); setTocando(false); }}
          onEnded={(e) => { parouDeTocar(e.target); setTocando(false); setTempo(0); }}
        />
      )}
    </div>
  );
}

export function Anexos({ anexos, aoAbrir, minha = false, aoOuvir = null }) {
  if (!anexos || anexos.length === 0) return null;
  const midias = anexos.filter((a) => a.tipo === "imagem" || a.tipo === "video");
  const audios = anexos.filter((a) => a.tipo === "audio");
  const outros = anexos.filter((a) => !["imagem", "video", "audio"].includes(a.tipo));
  return (
    <>
      {midias.length > 0 && <div className="pchat-midias">{midias.map((a) => <Miniatura key={a.id} anexo={a} aoAbrir={() => aoAbrir(anexos.indexOf(a))} />)}</div>}
      {audios.map((a) => <Audio key={a.id} anexo={a} minha={minha} aoOuvir={aoOuvir} />)}
      {outros.map((a) => (
        <button key={a.id} type="button" className="pchat-arquivo" onClick={() => aoAbrir(anexos.indexOf(a))}>
          <span className={`tipo ${a.tipo}`}>{SIGLA_DO_TIPO[a.tipo]}</span>
          <span className="meta">
            <b>{a.nome}</b>
            <span>{ROTULO_DO_TIPO[a.tipo]} · {tamanhoLegivel(a.tamanho)} · {a.abre_no_visualizador ? "abre aqui" : "baixa"}</span>
          </span>
        </button>
      ))}
    </>
  );
}

export function Visualizador({ anexos, indice, aoFechar }) {
  const [atual, setAtual] = useState(indice);
  const [ampliada, setAmpliada] = useState(false);
  const anexo = anexos[atual];
  const url = useBlob(anexo);
  useEffect(() => {
    const aoTeclar = (e) => {
      if (e.key === "Escape") { e.preventDefault(); aoFechar(); }
      else if (e.key === "ArrowRight") setAtual((i) => Math.min(i + 1, anexos.length - 1));
      else if (e.key === "ArrowLeft") setAtual((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [anexos.length, aoFechar]);
  if (!anexo) return null;
  const baixar = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = anexo.nome;
    a.click();
  };
  return (
    <div className="pchat-visualizador" role="dialog" aria-modal="true" aria-label={anexo.nome}>
      <div className="barra">
        <b>{anexo.nome}</b>
        <span style={{ opacity: .7, fontSize: 12.5 }}>{ROTULO_DO_TIPO[anexo.tipo]} · {tamanhoLegivel(anexo.tamanho)}{anexos.length > 1 ? ` · ${atual + 1}/${anexos.length}` : ""}</span>
        {anexos.length > 1 && (
          <>
            <button type="button" className="pchat-btn" aria-label="Anterior" disabled={atual === 0} onClick={() => setAtual((i) => i - 1)}><Ic d={ICONE.chevL} size={18} /></button>
            <button type="button" className="pchat-btn" aria-label="Próximo" disabled={atual === anexos.length - 1} onClick={() => setAtual((i) => i + 1)}><Ic d={ICONE.chevR} size={18} /></button>
          </>
        )}
        <button type="button" className="pchat-btn" aria-label="Baixar" title="Baixar" disabled={!url} onClick={baixar}><Ic d={ICONE.download} size={18} /></button>
        <button type="button" className="pchat-btn" aria-label="Fechar" onClick={aoFechar}><Ic d={ICONE.x} size={18} /></button>
      </div>
      <div className="palco" onClick={(e) => e.target === e.currentTarget && aoFechar()}>
        {!url ? <div className="baixar">Abrindo…</div>
          : anexo.tipo === "imagem" ? <img src={url} alt={anexo.nome} className={ampliada ? "ampliada" : ""} style={{ cursor: ampliada ? "zoom-out" : "zoom-in" }} onClick={() => setAmpliada((v) => !v)} />
          : anexo.tipo === "video" ? <video src={url} controls autoPlay playsInline />
          : anexo.tipo === "audio" ? <audio src={url} controls autoPlay style={{ width: "min(520px, 100%)" }} />
          : anexo.tipo === "pdf" ? <iframe src={url} title={anexo.nome} />
          : (
            <div className="baixar">
              <div><b>{anexo.nome}</b><div style={{ opacity: .7 }}>{ROTULO_DO_TIPO[anexo.tipo]} · {tamanhoLegivel(anexo.tamanho)} · abre no aplicativo do arquivo</div></div>
              <button type="button" className="pchat-btn enviar texto" onClick={baixar}><Ic d={ICONE.download} /> Baixar</button>
            </div>
          )}
      </div>
    </div>
  );
}
